import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import workspaceService from "@/services/workspaceService";
import {
  Download,
  FileText,
  Folder,
  FolderOpen,
  Image as ImageIcon,
  Link2,
  Music2,
  RefreshCcw,
  Search,
  Type,
  Users,
  Video,
} from "lucide-react";

const contentTypes = [
  { key: "video", label: "Videos", icon: Video },
  { key: "audio", label: "Audio", icon: Music2 },
  { key: "text", label: "Texts", icon: Type },
  { key: "document", label: "Documents", icon: FileText },
  { key: "image", label: "Images", icon: ImageIcon },
  { key: "web_link", label: "Web Links", icon: Link2 },
];

function formatBytes(bytes = 0) {
  if (!bytes) {
    return "-";
  }

  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function AdminWorkspaces() {
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [activeType, setActiveType] = useState("video");

  const loadFolders = async () => {
    try {
      const data = await workspaceService.getFolders({
        admin: true,
        ownerId: ownerFilter !== "all" ? ownerFilter : undefined,
        search: searchQuery.trim() || undefined,
      });
      setFolders(data);
      return data;
    } catch (error) {
      console.error("Failed to load admin workspaces:", error);
      return [];
    }
  };

  useEffect(() => {
    loadFolders();
  }, []);

  useEffect(() => {
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerFilter]);

  const ownerOptions = useMemo(() => {
    const map = new Map();
    folders.forEach((folder) => {
      if (!map.has(folder.ownerId)) {
        map.set(folder.ownerId, folder.ownerName || "Unknown user");
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [folders]);

  const visibleFolders = useMemo(() => {
    let items = folders.filter((folder) => !folder.deletedAt);

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      items = items.filter((folder) => {
        return [folder.name, folder.ownerName, folder.ownerEmail]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
    }

    return items.sort((left, right) => new Date(right.updatedAt) - new Date(left.updatedAt));
  }, [folders, searchQuery]);

  useEffect(() => {
    if (!visibleFolders.length) {
      setSelectedFolderId(null);
      return;
    }

    const stillExists = visibleFolders.some((folder) => folder.id === selectedFolderId);
    if (!stillExists) {
      setSelectedFolderId(visibleFolders[0].id);
    }
  }, [selectedFolderId, visibleFolders]);

  const selectedFolder =
    visibleFolders.find((folder) => folder.id === selectedFolderId) || null;

  const itemsByType = useMemo(() => {
    const counts = {};
    contentTypes.forEach((type) => {
      counts[type.key] = 0;
    });

    (selectedFolder?.items || []).forEach((item) => {
      if (Object.prototype.hasOwnProperty.call(counts, item.type)) {
        counts[item.type] += 1;
      }
    });

    return counts;
  }, [selectedFolder]);

  const visibleItems = useMemo(() => {
    return (selectedFolder?.items || [])
      .filter((item) => item.type === activeType)
      .sort((left, right) => new Date(right.createdAt) - new Date(left.createdAt));
  }, [activeType, selectedFolder]);

  const stats = useMemo(() => {
    const totalFolders = folders.filter((folder) => !folder.deletedAt).length;
    const totalFiles = folders.reduce(
      (count, folder) => count + (folder.deletedAt ? 0 : (folder.items || []).length),
      0
    );
    const totalUsers = new Set(
      folders.filter((folder) => !folder.deletedAt).map((folder) => folder.ownerId)
    ).size;

    return { totalFolders, totalFiles, totalUsers };
  }, [folders]);

  const EmptyTypeIcon =
    contentTypes.find((type) => type.key === activeType)?.icon || FolderOpen;

  return (
    <div className="space-y-6" data-testid="admin-workspaces">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">User Workspaces</h1>
          <p className="text-slate-400">
            Admin-only access to all user folders and uploaded workspace content.
          </p>
        </div>

        <Button
          onClick={loadFolders}
          variant="outline"
          className="border-slate-500 bg-slate-800 text-white hover:bg-slate-700"
        >
          <RefreshCcw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-700 bg-slate-800/50 p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-purple-500/15 text-purple-300">
              <Folder className="h-6 w-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{stats.totalFolders}</div>
              <div className="text-sm text-slate-400">Total folders</div>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-700 bg-slate-800/50 p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-500/15 text-blue-300">
              <FileText className="h-6 w-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{stats.totalFiles}</div>
              <div className="text-sm text-slate-400">Total files/items</div>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-700 bg-slate-800/50 p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-500/15 text-emerald-300">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{stats.totalUsers}</div>
              <div className="text-sm text-slate-400">Users with work folders</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
        <div className="rounded-2xl border border-slate-700 bg-slate-800/50 p-4">
          <div className="mb-4 flex flex-col gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search folder or user..."
                className="border-slate-600 bg-slate-900 pl-10 text-white placeholder:text-slate-500"
              />
            </div>

            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="border-slate-600 bg-slate-900 text-white">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900 text-white">
                <SelectItem value="all">All users</SelectItem>
                {ownerOptions.map((owner) => (
                  <SelectItem key={owner.value} value={owner.value}>
                    {owner.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="max-h-[720px] space-y-3 overflow-y-auto pr-1">
            {visibleFolders.length > 0 ? (
              visibleFolders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  onClick={() => setSelectedFolderId(folder.id)}
                  className={`w-full rounded-2xl border p-4 text-left transition ${
                    selectedFolderId === folder.id
                      ? "border-purple-500/60 bg-slate-700/60"
                      : "border-slate-700 bg-slate-900/65 hover:border-slate-500"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-lg font-semibold text-white">
                        {folder.name}
                      </div>
                      <div className="mt-1 text-sm text-slate-400">
                        {folder.ownerName}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {(folder.items || []).length} items • {formatDate(folder.updatedAt)}
                      </div>
                    </div>
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-800 text-purple-300">
                      <FolderOpen className="h-5 w-5" />
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-600 p-8 text-center text-slate-400">
                No user folders found
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-700 bg-slate-800/50 p-5">
          {selectedFolder ? (
            <>
              <div className="flex flex-col gap-4 border-b border-slate-700 pb-5">
                <div className="flex items-center gap-4">
                  <div className="grid h-16 w-16 place-items-center rounded-2xl bg-purple-500/15 text-purple-300">
                    <Folder className="h-8 w-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white">{selectedFolder.name}</h2>
                    <p className="mt-1 text-sm text-slate-400">
                      Owner: {selectedFolder.ownerName} • {(selectedFolder.items || []).length} items
                    </p>
                    <p className="text-xs text-slate-500">
                      Created {formatDate(selectedFolder.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {contentTypes.map((type) => {
                    const Icon = type.icon;
                    const active = activeType === type.key;
                    return (
                      <button
                        key={type.key}
                        type="button"
                        onClick={() => setActiveType(type.key)}
                        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                          active
                            ? "bg-purple-500/20 text-purple-200 border border-purple-500/40"
                            : "border border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {type.label}
                        <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs text-slate-300">
                          {itemsByType[type.key] || 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5">
                {visibleItems.length > 0 ? (
                  <div className="overflow-hidden rounded-2xl border border-slate-700">
                    <Table>
                      <TableHeader className="bg-slate-900">
                        <TableRow className="border-slate-700 hover:bg-transparent">
                          <TableHead className="px-5 py-4 text-slate-400">Name</TableHead>
                          <TableHead className="px-5 py-4 text-slate-400">Size</TableHead>
                          <TableHead className="px-5 py-4 text-slate-400">Added On</TableHead>
                          <TableHead className="px-5 py-4 text-right text-slate-400">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleItems.map((item) => (
                          <TableRow key={item.id} className="border-slate-700 hover:bg-slate-900/70">
                            <TableCell className="px-5 py-4">
                              <div className="font-medium text-white">{item.name}</div>
                              {item.url ? (
                                <div className="mt-1 text-xs text-slate-500">{item.url}</div>
                              ) : null}
                            </TableCell>
                            <TableCell className="px-5 py-4 text-slate-300">
                              {formatBytes(item.size)}
                            </TableCell>
                            <TableCell className="px-5 py-4 text-slate-300">
                              {formatDate(item.createdAt)}
                            </TableCell>
                            <TableCell className="px-5 py-4 text-right">
                              {item.url ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => workspaceService.downloadItem(item)}
                                  className="border-slate-600 bg-slate-900 text-white hover:bg-slate-700"
                                >
                                  Open
                                </Button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => workspaceService.downloadItem(item)}
                                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-white"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-600 px-6 py-16 text-center">
                    <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-slate-900 text-slate-300">
                      <EmptyTypeIcon className="h-8 w-8" />
                    </div>
                    <h3 className="mt-6 text-2xl font-semibold text-white">
                      No {contentTypes.find((type) => type.key === activeType)?.label.toLowerCase()} yet
                    </h3>
                    <p className="mt-2 text-slate-400">
                      This folder does not have any items for the selected content type.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-600 px-6 py-20 text-center">
              <FolderOpen className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-4 text-2xl font-semibold text-white">Select a folder</h3>
              <p className="mt-2 text-slate-400">
                Choose any user folder on the left to inspect its files.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
